import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ChevronLeft, ChevronRight, Loader2, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { http, errMsg, imgUrl } from "@/lib/api";

export const ImageManager = ({ value, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await http.post("/admin/upload", fd);
      const urls = Array.isArray(data?.urls) ? data.urls : [];
      onChange([...value, ...urls]);
      toast.success(`${urls.length} foto caricate`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const addLink = () => {
    if (!link.trim()) return;
    onChange([...value, link.trim()]);
    setLink("");
  };

  return (
    <div data-testid="image-manager">
      <div className="flex flex-wrap items-center gap-3">
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="lf-file-input" />
        <Button type="button" onClick={() => input.current?.click()} disabled={busy} className="gap-2" data-testid="lf-upload-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Caricamento…" : "Carica foto"}
        </Button>
        <span className="text-xs text-muted-foreground">Puoi selezionarne più di una. La prima è la copertina.</span>
      </div>
      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
          {value.map((u, i) => (
            <div key={u + i} className="relative group border bg-gray-50" data-testid={`lf-image-${i}`}>
              <img src={imgUrl(u)} alt="" className="w-full aspect-[4/3] object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 bg-[var(--iv)] text-white text-[10px] font-bold px-1.5 py-0.5">COPERTINA</span>}
              <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} aria-label="Rimuovi" data-testid={`lf-image-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center text-red-600 shadow"><X className="w-3.5 h-3.5" /></button>
              <div className="absolute bottom-1 inset-x-1 flex justify-between">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Sposta a sinistra" data-testid={`lf-image-left-${i}`}
                  className="w-6 h-6 rounded-full bg-white/90 grid place-items-center shadow disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} aria-label="Sposta a destra" data-testid={`lf-image-right-${i}`}
                  className="w-6 h-6 rounded-full bg-white/90 grid place-items-center shadow disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Oppure incolla il link di un'immagine" className="pl-8" data-testid="lf-image-link-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }} />
        </div>
        <Button type="button" variant="outline" onClick={addLink} data-testid="lf-image-link-add">Aggiungi</Button>
      </div>
    </div>
  );
};
